import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const SESSION_HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * useActiveBillingSession
 *
 * Tracks the user's active session for per-active-user billing.
 * - Creates/updates a record in active_billing_sessions on login
 * - Sends heartbeats every 5 minutes while the user is active
 * - Marks the session as inactive on logout or tab close
 *
 * BILLING LOGIC: Any user who logs in at any point within the calendar month
 * counts as a billable user for that month, regardless of how long they stay logged in.
 */
const useActiveBillingSession = () => {
  const { user, userProfile } = useAuth();
  const sessionTokenRef = useRef(null);
  const heartbeatRef = useRef(null);
  const isRegisteredRef = useRef(false);

  const getOrCreateSessionToken = useCallback(() => {
    let token = localStorage.getItem('sj_session_token');
    if (!token) {
      token = `sj_${Date.now()}_${Math.random()?.toString(36)?.slice(2)}`;
      localStorage.setItem('sj_session_token', token);
    }
    sessionTokenRef.current = token;
    return token;
  }, []);

  const registerSession = useCallback(async () => {
    if (!user?.id || !userProfile?.organization_id || isRegisteredRef?.current) return;
    let token = getOrCreateSessionToken();
    isRegisteredRef.current = true;

    try {
      // Record the login. logged_in_at captures when the user logged in this month.
      // Billing counts any distinct user_id with a logged_in_at within the current month.
      await supabase
        ?.from('active_billing_sessions')
        ?.upsert(
          {
            user_id: user?.id,
            organization_id: userProfile?.organization_id,
            session_token: token,
            logged_in_at: new Date()?.toISOString(),
            last_active_at: new Date()?.toISOString(),
            is_active: true,
          },
          { onConflict: 'session_token' }
        );
    } catch (err) {
      console.warn('[Billing] Failed to register session:', err);
    }
  }, [user?.id, userProfile?.organization_id, getOrCreateSessionToken]);

  const sendHeartbeat = useCallback(async () => {
    let token = sessionTokenRef?.current || localStorage.getItem('sj_session_token');
    if (!token || !user?.id) return;

    try {
      await supabase
        ?.from('active_billing_sessions')
        ?.update({ last_active_at: new Date()?.toISOString(), is_active: true })
        ?.eq('session_token', token)
        ?.eq('user_id', user?.id);
    } catch (err) {
      console.warn('[Billing] Heartbeat failed:', err);
    }
  }, [user?.id]);

  const endSession = useCallback(async () => {
    let token = sessionTokenRef?.current || localStorage.getItem('sj_session_token');
    if (!token || !user?.id) return;

    try {
      await supabase
        ?.from('active_billing_sessions')
        ?.update({
          logged_out_at: new Date()?.toISOString(),
          is_active: false,
        })
        ?.eq('session_token', token)
        ?.eq('user_id', user?.id);
    } catch (err) {
      console.warn('[Billing] Failed to end session:', err);
    }
    isRegisteredRef.current = false;
  }, [user?.id]);

  // Register session when user logs in
  useEffect(() => {
    if (user?.id && userProfile?.organization_id) {
      registerSession();
    }
  }, [user?.id, userProfile?.organization_id, registerSession]);

  // Heartbeat every 5 minutes
  useEffect(() => {
    if (!user?.id) return;

    heartbeatRef.current = setInterval(sendHeartbeat, SESSION_HEARTBEAT_INTERVAL);
    return () => clearInterval(heartbeatRef?.current);
  }, [user?.id, sendHeartbeat]);

  // End session on logout (user becomes null)
  useEffect(() => {
    if (!user?.id && isRegisteredRef?.current) {
      endSession();
    }
  }, [user?.id, endSession]);

  // End session on tab/window close
  useEffect(() => {
    const handleBeforeUnload = () => {
      let token = sessionTokenRef?.current || localStorage.getItem('sj_session_token');
      if (!token || !user?.id) return;
      const payload = JSON.stringify({
        session_token: token,
        user_id: user?.id,
        logged_out_at: new Date()?.toISOString(),
        is_active: false,
      });
      navigator?.sendBeacon?.(
        `${import.meta.env?.VITE_SUPABASE_URL}/rest/v1/active_billing_sessions?session_token=eq.${token}`,
        new Blob([payload], { type: 'application/json' })
      );
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user?.id]);

  return { endSession, sendHeartbeat };
};

export default useActiveBillingSession;
